import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useLayoutEffect,
  useMemo,
  useState,
  type ReactNode,
} from "react";
import {
  applyThemePreference,
  buildThemePreferenceFromPreset,
  DEFAULT_THEME_PRESET_ID,
  loadStoredThemePreference,
  loadStoredThemePreferenceForBootstrap,
  loadStoredThemePreferenceForUser,
  saveThemePreferenceForScope,
  sanitizeThemePreference,
  type ThemeBasePalette,
  type ThemePreference,
  type ThemePreset,
  type ThemePresetId,
  type VisualMode,
  THEME_PRESETS,
} from "../../styles/themePalette";

type ThemeContextValue = {
  /** Logged-in user id; theme is persisted under this scope and synced to the server. */
  accountUserId: string | null;
  setAccountUserId: (userId: string | null) => void;
  preference: ThemePreference;
  presets: readonly ThemePreset[];
  currentPreset: ThemePreset | null;
  visualMode: VisualMode;
  setPreset: (presetId: ThemePresetId) => void;
  setVisualMode: (mode: VisualMode) => void;
  updatePalette: (patch: Partial<ThemeBasePalette>) => void;
  updateTactility: (next: number) => void;
  setThemePreference: (next: ThemePreference) => void;
  resetTheme: () => void;
};

const ThemeContext = createContext<ThemeContextValue | null>(null);

export function ThemeProvider({ children }: { children: ReactNode }) {
  const [accountUserId, setAccountUserId] = useState<string | null>(null);
  const [preference, setPreference] = useState<ThemePreference>(() => loadStoredThemePreferenceForBootstrap());

  /** Load from the correct localStorage scope before save runs — avoids clobbering the per-user key with global/default state when accountUserId becomes non-null. */
  useLayoutEffect(() => {
    if (accountUserId) {
      setPreference(loadStoredThemePreferenceForUser(accountUserId));
    } else {
      setPreference(loadStoredThemePreference());
    }
  }, [accountUserId]);

  useEffect(() => {
    const normalized = applyThemePreference(preference);
    saveThemePreferenceForScope(accountUserId, normalized);
  }, [accountUserId, preference]);

  const setPreset = useCallback((presetId: ThemePresetId) => {
    setPreference(buildThemePreferenceFromPreset(presetId));
  }, []);

  const setVisualMode = useCallback((mode: VisualMode) => {
    setPreference((prev) =>
      sanitizeThemePreference({
        ...prev,
        visualMode: mode,
      }),
    );
  }, []);

  const updatePalette = useCallback((patch: Partial<ThemeBasePalette>) => {
    setPreference((prev) =>
      sanitizeThemePreference({
        presetId: "custom",
        palette: {
          ...prev.palette,
          ...patch,
        },
        visualMode: prev.visualMode,
        tactility: prev.tactility,
        tokenOverrides: prev.tokenOverrides,
      }),
    );
  }, []);

  const updateTactility = useCallback((next: number) => {
    setPreference((prev) =>
      sanitizeThemePreference({
        ...prev,
        tactility: next,
      }),
    );
  }, []);

  const setThemePreference = useCallback((next: ThemePreference) => {
    setPreference(sanitizeThemePreference(next));
  }, []);

  const resetTheme = useCallback(() => {
    setPreference(buildThemePreferenceFromPreset(DEFAULT_THEME_PRESET_ID));
  }, []);

  const currentPreset = useMemo(
    () => (preference.presetId === "custom" ? null : THEME_PRESETS.find((preset) => preset.id === preference.presetId) ?? null),
    [preference.presetId],
  );

  const value = useMemo(
    () => ({
      accountUserId,
      setAccountUserId,
      preference,
      presets: THEME_PRESETS,
      currentPreset,
      visualMode: preference.visualMode,
      setPreset,
      setVisualMode,
      updatePalette,
      updateTactility,
      setThemePreference,
      resetTheme,
    }),
    [
      accountUserId,
      currentPreset,
      preference,
      resetTheme,
      setPreset,
      setVisualMode,
      setThemePreference,
      updatePalette,
      updateTactility,
    ],
  );

  return <ThemeContext.Provider value={value}>{children}</ThemeContext.Provider>;
}

export function useTheme(): ThemeContextValue {
  const ctx = useContext(ThemeContext);
  if (!ctx) throw new Error("useTheme outside ThemeProvider");
  return ctx;
}
